import {
  clearIndexedDbPersistence,
  collection,
  doc,
  getDoc,
  getFirestore,
  initializeFirestore,
  onSnapshot,
  persistentLocalCache,
  persistentMultipleTabManager,
  query,
  where,
  type Firestore,
} from "firebase/firestore"

import { app } from "./firebaseApp"
import { APP_CHECK_STATUS_EVENT, dispatchAppCheckStatus, isLocalHost } from "./firebaseAppCheckStatus"

function isPersistenceCorruptionError(err: any): boolean {
  const message = err?.message || String(err || "")
  return (
    message.includes("potential corruption of the IndexedDB") ||
    message.includes("refusing to open IndexedDB database due to potential corruption") ||
    message.includes("Version change transaction was aborted")
  )
}

async function handlePersistenceCorruption(firestoreInstance?: Firestore) {
  if (typeof window === "undefined") return
  const recoveryKey = "firestorePersistenceRecovery"
  const hasRetried = sessionStorage.getItem(recoveryKey)
  if (hasRetried) return
  sessionStorage.setItem(recoveryKey, "1")
  try {
    const targetDb = firestoreInstance || db || getFirestore(app)
    await clearIndexedDbPersistence(targetDb)
  } catch (clearErr) {
    console.warn("Unable to clear Firestore persistence cache after corruption.", clearErr)
  } finally {
    window.location.reload()
  }
}

const createFirestore = () => {
  if (process.env.NODE_ENV !== "production") {
    return getFirestore(app)
  }

  try {
    return initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager(),
      }),
    })
  } catch (err) {
    console.warn("Failed to initialize Firestore with persistent cache; falling back to memory cache.", err)
    if (isPersistenceCorruptionError(err)) {
      void handlePersistenceCorruption()
    }
    return getFirestore(app)
  }
}

export const db = createFirestore()

if (typeof window !== "undefined") {
  const corruptionListener = (event: any) => {
    const reason = event?.reason || event?.error || event
    if (isPersistenceCorruptionError(reason)) {
      event?.preventDefault?.()
      void handlePersistenceCorruption()
    }
  }

  const appCheckRejectionListener = (event: PromiseRejectionEvent) => {
    if (!isLocalHost) {
      return
    }

    const reason = event?.reason
    const code = reason?.code
    if (typeof code !== "string" || !code.startsWith("appCheck/")) {
      return
    }

    event.preventDefault?.()

    const message = typeof reason?.message === "string" ? reason.message : String(reason || "App Check failed")
    dispatchAppCheckStatus({
      status: "error",
      message,
      code,
    })
  }

  window.addEventListener("unhandledrejection", corruptionListener)
  window.addEventListener("error", corruptionListener)
  window.addEventListener("unhandledrejection", appCheckRejectionListener)
}

export { APP_CHECK_STATUS_EVENT, collection, doc, getDoc, onSnapshot, query, where }

export type { CollectionReference, DocumentData } from "firebase/firestore"
